import { brand } from './brand';
import { typeLabel } from './fieldTypes';

// Pre-fill sources for a field's `map` (build plan §2.4). Same data as the
// server's PREFILL_SOURCES. `ok` types are a natural fit; `warn` types work
// with the note shown; any other type can't use the source.

const src = (key, label, group, ok, warn = {}) => ({
  key,
  label,
  group,
  ok,
  warn,
});

const PROFILE = 'Profile';
const RACING = 'Racing';

export const PREFILL_SOURCES = [
  src('first_name', 'First Name', PROFILE, ['short_answer']),
  src('middle_name', 'Middle Name', PROFILE, ['short_answer']),
  src('last_name', 'Last Name', PROFILE, ['short_answer']),
  src('suffix', 'Suffix', PROFILE, ['short_answer', 'dropdown', 'radio']),
  src('full_name', 'Full Name', PROFILE, ['name'], {
    short_answer: 'Fills one line, e.g. “Dale Reynolds”',
  }),
  src('date_of_birth', 'Date of Birth', PROFILE, ['datetime'], {
    short_answer: 'Fills as MM/DD/YYYY with no date check',
  }),
  src('email', 'Email', PROFILE, ['email'], {
    short_answer: "An edited value isn't checked as an email",
  }),
  src('cellphone', 'Mobile Number', PROFILE, ['phone'], {
    short_answer: 'No formatting or checks',
  }),
  src('address', 'Address', PROFILE, ['address'], {
    short_answer: 'Street line only',
    long_answer: 'The full address on separate lines',
  }),
  src('city', 'City', PROFILE, ['short_answer']),
  src('state', 'State', PROFILE, ['dropdown', 'radio'], {
    short_answer: 'Two-letter code',
  }),
  src('zipcode', 'ZIP Code', PROFILE, ['short_answer', 'number']),
  src('class_or_division', 'Division / Class', RACING, ['dropdown', 'radio'], {
    short_answer: 'Free text, less consistent in reports',
  }),
  src('membership_number', 'Membership Number', RACING, ['short_answer'], {
    number: 'Only works if every member number is digits',
  }),
];

export const PREFILL_GROUPS = [PROFILE, RACING];

const byKey = PREFILL_SOURCES.reduce((acc, s) => {
  acc[s.key] = s;
  return acc;
}, {});

export const sourceLabel = (key) => (byKey[key] ? byKey[key].label : key);

// 'ok' | 'warn' | null for a source on a field type.
export const prefillFit = (sourceKey, fieldType) => {
  const s = byKey[sourceKey];
  if (!s) return null;
  if (s.ok.includes(fieldType)) return 'ok';
  if (Object.prototype.hasOwnProperty.call(s.warn, fieldType)) return 'warn';
  return null;
};

// The caveat shown for a 'warn' fit, else null.
export const prefillNote = (sourceKey, fieldType) =>
  prefillFit(sourceKey, fieldType) === 'warn'
    ? byKey[sourceKey].warn[fieldType]
    : null;

// Sources a field type can use (fit ok or warn), in table order.
export const compatibleSources = (fieldType) =>
  PREFILL_SOURCES.filter((s) => prefillFit(s.key, fieldType) !== null);

// Label guesses, case-insensitive, first match wins.
const LABEL_RULES = [
  [/first name/i, 'first_name'],
  [/last name|surname/i, 'last_name'],
  [/middle/i, 'middle_name'],
  [/suffix/i, 'suffix'],
  [/birth|dob/i, 'date_of_birth'],
  [/e-mail|email/i, 'email'],
  [/phone|cell|mobile/i, 'cellphone'],
  [/zip|postal/i, 'zipcode'],
  [/city|town/i, 'city'],
  [/state/i, 'state'],
  [/address|street/i, 'address'],
  [/division|class/i, 'class_or_division'],
  [/member (number|no|#|id)/i, 'membership_number'],
  [/name/i, 'full_name'],
];

// The source the label points at, when that source suits the type.
export const labelSource = (label, fieldType) => {
  const text = String(label || '');
  const rule = LABEL_RULES.find(([rx]) => rx.test(text));
  if (!rule) return null;
  return prefillFit(rule[1], fieldType) ? rule[1] : null;
};

// Best source for a field: the label's match when it suits the type, else
// the type's first natural fit. A type with only 'warn' sources (e.g.
// long_answer) falls back to its first one; no source at all → null.
export const guessSource = (label, fieldType) => {
  const fromLabel = labelSource(label, fieldType);
  if (fromLabel) return fromLabel;
  const ok = PREFILL_SOURCES.find((s) => prefillFit(s.key, fieldType) === 'ok');
  if (ok) return ok.key;
  const any = compatibleSources(fieldType)[0];
  return any ? any.key : null;
};

const STATE_CODE = /^[A-Z]{2}$/;

const result = (level, message, suggest = null) => ({
  level,
  message,
  suggest,
});

// Live check shown under "Maps to" (plan §2.4). null when unmapped.
export const checkMapping = (field) => {
  if (!field || !field.map) return null;
  const { map, type: fieldType } = field;
  const fit = prefillFit(map, fieldType);
  const source = sourceLabel(map);

  if (!fit) {
    return result(
      'blocked',
      `Can't pre-fill a ${typeLabel(fieldType)} field from ${source}`
    );
  }
  if (fit === 'warn') return result('warn', prefillNote(map, fieldType));

  const suggested = labelSource(field.label, fieldType);
  if (suggested && suggested !== map) {
    return result(
      'warn',
      `The label looks like ${sourceLabel(suggested)}`,
      suggested
    );
  }

  const isChoice = fieldType === 'dropdown' || fieldType === 'radio';
  if (isChoice && map === 'state') {
    const options = (field.config && field.config.options) || [];
    if (options.some((o) => !STATE_CODE.test(String(o)))) {
      return result('warn', 'Use two-letter state codes as the options');
    }
  } else if (isChoice) {
    return result(
      'warn',
      `Pre-fill only works when an option matches the person's ${
        brand().name
      } value exactly`
    );
  }

  return result(
    'ok',
    `Pre-fills from ${source}. They can still change it before submitting.`
  );
};
